import { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from 'date-fns';

import { useGlobalTranslation } from 'modules/i18n';

export function useTimeLeftText(targetDate: Date | null): string {
  const { t, keys } = useGlobalTranslation();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  return useMemo(() => {
    if (!targetDate) {
      return '';
    }

    const now = new Date();

    const time = (() => {
      const daysDiff = differenceInDays(targetDate, now);
      if (daysDiff >= 1) {
        return t(keys.time.days, { value: daysDiff });
      }

      const hoursDiff = differenceInHours(targetDate, now);
      if (hoursDiff >= 1) {
        return t(keys.time.hours, { value: hoursDiff });
      }

      const minutesDiff = differenceInMinutes(targetDate, now);
      if (minutesDiff >= 1) {
        return t(keys.time.minutes, { value: minutesDiff });
      }

      const secondsLeft = differenceInSeconds(targetDate, now);
      return secondsLeft > 0
        ? t(keys.time.seconds, { value: secondsLeft })
        : '';
    })();

    return isMd
      ? t(keys.common.unlockTime, { time })
      : t(keys.common.timeLeft, { time });
  }, [
    targetDate,
    isMd,
    t,
    keys.common.unlockTime,
    keys.common.timeLeft,
    keys.time.seconds,
    keys.time.days,
    keys.time.hours,
    keys.time.minutes,
  ]);
}
